/* eslint-disable max-lines */
import { type ReactNode, useCallback, useMemo } from "react"

import { Handle, NodeResizer, Position, useReactFlow, useStore } from "@xyflow/react"

import type { Node, NodeProps } from "@xyflow/react"

import { ArrowLeft, ArrowRight, Repeat, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { CATEGORY_TONES, STATUS_TONES } from "@/components/ui/tone-classes"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { NodeEditDialog } from "./node-edit-dialog"

const MIN_WIDTH = 400
const MIN_HEIGHT = 300
const DEFAULT_WIDTH = 500
const DEFAULT_HEIGHT = 350
const SETTINGS_HEIGHT = 120

type LoopMode = "count" | "array"
type ResultMode = "collect" | "join" | "last" | "sum"

const LOOP_MODE_INFO: Record<LoopMode, { label: string; description: string; example: string }> = {
  count: {
    label: "Run N times",
    description: "Runs the loop a fixed number of times",
    example: "e.g. 5 iterations → runs 5 times with {{loop.index}} = 1, 2, 3, 4, 5",
  },
  array: {
    label: "For each item",
    description: "Iterates over each item in an array/list",
    example: 'e.g. ["a", "b", "c"] → runs 3 times with {{loop.item}} = a, b, c',
  },
}

const RESULT_MODE_INFO: Record<
  ResultMode,
  { label: string; description: string; example: string }
> = {
  collect: {
    label: "Collect all",
    description: "Returns all outputs as a JSON array",
    example: '["result1", "result2", "result3"]',
  },
  join: {
    label: "Join as text",
    description: "Concatenates outputs with a separator",
    example: "result1\\nresult2\\nresult3",
  },
  last: {
    label: "Keep last only",
    description: "Only keeps the output from the final iteration",
    example: "result3 (last iteration output)",
  },
  sum: {
    label: "Sum values",
    description: "Adds numeric values together",
    example: "5 + 10 + 15 = 30",
  },
}

type LoopSettingSelectProps = {
  items: ReactNode
  label: string
  value: string
  onValueChange: (value: string) => void
}

function LoopSettingSelect({ items, label, value, onValueChange }: LoopSettingSelectProps) {
  return (
    <div className="grid gap-1">
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="h-7 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>{items}</SelectContent>
      </Select>
    </div>
  )
}

export function LoopContainerNode({ id, data, selected }: NodeProps) {
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const readOnly = useWorkflowStore((state) => state.readOnly)
  const dropTargetLoopId = useWorkflowStore((state) => state.dropTargetLoopId)
  const invalidConnectionLoopId = useWorkflowStore((state) => state.invalidConnectionLoopId)
  const invalidConnectionType = useWorkflowStore((state) => state.invalidConnectionType)
  const { deleteElements, getNodes } = useReactFlow()
  const { isExecuting, isCompleted, output, loopProgress, isTestRunActive } =
    useNodeExecutionState(id)

  // Check if this loop is the current drop target
  const isDropTarget = dropTargetLoopId === id

  // Check if this loop's handles should be highlighted due to invalid connection attempt
  const isInputHighlighted = invalidConnectionLoopId === id && invalidConnectionType === "input"
  const isOutputHighlighted = invalidConnectionLoopId === id && invalidConnectionType === "output"

  // Get child nodes count for display
  const childNodesCount = useStore((state) => {
    const nodes = state.nodes as Node[]
    return nodes.filter((n) => n.parentId === id).length
  })

  // Check if this loop has incoming edges (connected to input handle)
  const hasIncomingEdge = useWorkflowStore((state) => state.edges.some((e) => e.target === id))

  // Check if this loop has outgoing edges (connected from output handle)
  const hasOutgoingEdge = useWorkflowStore((state) => state.edges.some((e) => e.source === id))

  const handleModeChange = useCallback(
    (value: string) => {
      updateNodeData(id, { mode: value })
    },
    [id, updateNodeData]
  )

  const handleIterationsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value) || 1
      updateNodeData(id, { iterations: Math.min(Math.max(value, 1), 100) })
    },
    [id, updateNodeData]
  )

  const handleResultModeChange = useCallback(
    (value: string) => {
      updateNodeData(id, { resultMode: value })
    },
    [id, updateNodeData]
  )

  const handleJoinSeparatorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateNodeData(id, { joinSeparator: e.target.value })
    },
    [id, updateNodeData]
  )

  const handleInlineItemsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateNodeData(id, { inlineItems: e.target.value })
    },
    [id, updateNodeData]
  )

  const handleDelete = useCallback(() => {
    if (readOnly) return
    // Delete the container and all its children
    const allNodes = getNodes()
    const childNodes = allNodes.filter((n) => n.parentId === id)
    const nodesToDelete = [{ id }, ...childNodes.map((n) => ({ id: n.id }))]
    void deleteElements({ nodes: nodesToDelete })
  }, [id, getNodes, deleteElements, readOnly])

  const mode = (data.mode as LoopMode | undefined) ?? "count"
  const iterations = (data.iterations as number | undefined) ?? 3
  const resultMode = (data.resultMode as ResultMode | undefined) ?? "collect"
  const joinSeparator = (data.joinSeparator as string | undefined) ?? "\\n"
  const inlineItems = (data.inlineItems as string | undefined) ?? ""
  const hasOutput = isCompleted && !!output

  // Show warning when in array mode with no manual items
  const showArrayWarning = mode === "array" && inlineItems.trim().length === 0

  // Show connection hints when loop is empty and has no connections
  const showInputHint = childNodesCount === 0 && !hasIncomingEdge && !showArrayWarning
  const showOutputHint = childNodesCount === 0 && !hasOutgoingEdge

  // Summary text for the header
  const summaryText = useMemo(() => {
    const modeText = mode === "count" ? `${iterations}x` : "each item"
    const resultText =
      resultMode === "join"
        ? `Join (${joinSeparator === "\\n" ? "newline" : joinSeparator})`
        : RESULT_MODE_INFO[resultMode].label
    return `${modeText} → ${resultText}`
  }, [mode, iterations, resultMode, joinSeparator])

  const loopModeItems = Object.entries(LOOP_MODE_INFO).map(([value, info]) => (
    <SelectItem key={value} value={value}>
      {info.label}
    </SelectItem>
  ))
  const resultModeItems = Object.entries(RESULT_MODE_INFO).map(([value, info]) => (
    <SelectItem key={value} value={value}>
      {info.label}
    </SelectItem>
  ))

  const settingsEditor = (
    <div className="grid gap-3">
      <LoopSettingSelect
        items={loopModeItems}
        label="Loop Mode"
        value={mode}
        onValueChange={handleModeChange}
      />
      {mode === "count" && (
        <div className="grid gap-1">
          <Label className="text-xs">Iterations</Label>
          <Input
            className="h-7 text-xs"
            max={100}
            min={1}
            type="number"
            value={iterations}
            onChange={handleIterationsChange}
          />
        </div>
      )}
      {mode === "array" && (
        <div className="grid gap-1">
          <Label className="text-xs">Items</Label>
          <Input
            className="h-7 text-xs"
            placeholder="a, b, c"
            value={inlineItems}
            onChange={handleInlineItemsChange}
          />
        </div>
      )}
      <LoopSettingSelect
        items={resultModeItems}
        label="Result Handling"
        value={resultMode}
        onValueChange={handleResultModeChange}
      />
      {resultMode === "join" && (
        <div className="grid gap-1">
          <Label className="text-xs">Separator</Label>
          <Input
            className="h-7 text-xs"
            placeholder="\n"
            value={joinSeparator}
            onChange={handleJoinSeparatorChange}
          />
        </div>
      )}
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <div
        className={cn(
          "bg-muted/50 relative rounded-lg border-2 border-dashed",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer",
          isDropTarget && "border-success bg-success/15 ring-success/50 border-solid ring-4"
        )}
        style={{
          width: (data.width as number) || DEFAULT_WIDTH,
          height: (data.height as number) || DEFAULT_HEIGHT,
          minWidth: MIN_WIDTH,
          minHeight: MIN_HEIGHT,
        }}
      >
        {/* Resizer - only show when selected */}
        <NodeResizer
          color="hsl(var(--primary))"
          handleClassName="!size-2 !rounded-full !border-2"
          isVisible={selected}
          lineClassName="!border-primary"
          minHeight={MIN_HEIGHT}
          minWidth={MIN_WIDTH}
          onResize={(_, params) => {
            updateNodeData(id, { width: params.width, height: params.height })
          }}
        />

        {/* Header with configuration */}
        <div
          className={cn(
            "flex items-center justify-between gap-2 rounded-t-md px-3 py-2",
            CATEGORY_TONES[3]
          )}
        >
          <div className="flex items-center gap-2">
            <NodeExecutionIndicator
              hasOutput={hasOutput}
              isCompleted={isCompleted}
              isExecuting={isExecuting}
            />
            <Repeat className="size-4" />
            <span className="text-sm font-medium">Loop</span>
            {/* Show iteration progress during execution */}
            {isTestRunActive && loopProgress ? (
              <span className="border-foreground bg-card text-foreground inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium">
                <span className="tabular-nums">
                  {loopProgress.current}/{loopProgress.total}
                </span>
              </span>
            ) : (
              <span className="text-xs">({summaryText})</span>
            )}
            <NodeEditDialog
              description="Edit loop container behavior in a larger workspace."
              title="Loop settings"
            >
              {settingsEditor}
            </NodeEditDialog>
          </div>

          {selected && !readOnly && (
            <Button
              className="nodrag -m-0.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </div>

        {/* Configuration panel - always visible */}
        <div className="nodrag border-border bg-card border-b px-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            {/* Loop Mode */}
            <div className="grid gap-1">
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Label className="cursor-help text-xs">Loop Mode</Label>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="font-medium">How the loop iterates</p>
                  <p className="text-muted-foreground text-xs">
                    Choose between fixed count or iterating over items
                  </p>
                </TooltipContent>
              </Tooltip>
              <Select value={mode} onValueChange={handleModeChange}>
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LOOP_MODE_INFO).map(([value, info]) => (
                    <Tooltip key={value} delayDuration={0}>
                      <TooltipTrigger asChild>
                        <SelectItem value={value}>{info.label}</SelectItem>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p className="font-medium">{info.description}</p>
                        <p className="text-muted-foreground mt-1 text-xs">{info.example}</p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Iterations (only for count mode) */}
            {mode === "count" && (
              <div className="grid gap-1">
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Label className="cursor-help text-xs">Iterations</Label>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="font-medium">Number of times to run</p>
                    <p className="text-muted-foreground text-xs">Max 100 iterations</p>
                  </TooltipContent>
                </Tooltip>
                <Input
                  className="h-7 text-xs"
                  max={100}
                  min={1}
                  type="number"
                  value={iterations}
                  onChange={handleIterationsChange}
                />
              </div>
            )}

            {/* Inline items (array mode) */}
            {mode === "array" && (
              <div className="grid gap-1">
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Label className="cursor-help text-xs">Items</Label>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="font-medium">Items to iterate over</p>
                    <p className="text-muted-foreground text-xs">
                      Comma-separated values, e.g. "a, b, c"
                    </p>
                  </TooltipContent>
                </Tooltip>
                <Input
                  className="h-7 text-xs"
                  id={`${id}-inlineItems`}
                  placeholder="a, b, c"
                  value={inlineItems}
                  onChange={handleInlineItemsChange}
                />
              </div>
            )}

            {/* Result Mode */}
            <div className="grid gap-1">
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Label className="cursor-help text-xs">Result Handling</Label>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="font-medium">How outputs are combined</p>
                  <p className="text-muted-foreground text-xs">
                    What happens to the result of each iteration
                  </p>
                </TooltipContent>
              </Tooltip>
              <Select value={resultMode} onValueChange={handleResultModeChange}>
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(RESULT_MODE_INFO).map(([value, info]) => (
                    <Tooltip key={value} delayDuration={0}>
                      <TooltipTrigger asChild>
                        <SelectItem value={value}>{info.label}</SelectItem>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p className="font-medium">{info.description}</p>
                        <p className="text-muted-foreground mt-1 font-mono text-xs">
                          {info.example}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Join Separator (only for join mode) */}
            {resultMode === "join" && (
              <div className="grid gap-1">
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Label className="cursor-help text-xs">Separator</Label>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="font-medium">Text between joined results</p>
                    <p className="text-muted-foreground text-xs">
                      Use \n for newline, or any text like ", "
                    </p>
                  </TooltipContent>
                </Tooltip>
                <Input
                  className="h-7 text-xs"
                  placeholder="\n"
                  value={joinSeparator}
                  onChange={handleJoinSeparatorChange}
                />
              </div>
            )}
          </div>

          {/* Help text */}
          <div className="text-muted-foreground mt-1.5 text-[10px]">
            {mode === "count"
              ? `Runs ${iterations} times. Use {{loop.index}} in system prompt to access the current iteration number.`
              : "Iterates over Items. Use {{loop.item}} in system prompt to access the current item value."}
          </div>
        </div>

        {/* Drop zone hint - positioned in the content area below settings */}
        {(childNodesCount === 0 || isDropTarget) && (
          <div
            style={{ top: SETTINGS_HEIGHT + 40 }}
            className={cn(
              "pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center",
              isDropTarget && "z-20"
            )}
          >
            <div
              className={cn(
                "text-center text-sm",
                isDropTarget
                  ? cn(STATUS_TONES.success.soft, "rounded-lg px-4 py-3 shadow-lg")
                  : "text-muted-foreground"
              )}
            >
              {isDropTarget ? (
                <>
                  <p className="font-medium">Release to add to loop</p>
                  <p className="text-xs opacity-80">Node will execute for each iteration</p>
                </>
              ) : (
                <>
                  <p>Drop nodes here</p>
                  <p className="text-xs">They will execute for each iteration</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Left Handle with warning/highlight indicator */}
        <Handle
          position={Position.Left}
          type="target"
          className={cn(
            "!bg-background !-left-2 !size-3 !border-2",
            isInputHighlighted && "loop-handle-highlighted",
            !isInputHighlighted && showArrayWarning && "!border-warning animate-pulse",
            !isInputHighlighted && !showArrayWarning && "!border-primary"
          )}
        />

        {/* Warning arrow indicator for missing iterable - positioned in content area */}
        {showArrayWarning && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <div
                className="group bg-warning/15 hover:bg-warning/25 absolute left-2 cursor-pointer rounded-full p-1.5 shadow-sm transition-[background-color,box-shadow,scale] hover:scale-110 hover:shadow-md"
                style={{ top: SETTINGS_HEIGHT + 60 }}
              >
                <ArrowLeft className="text-warning group-hover:text-warning size-4 animate-pulse group-hover:animate-none" />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs p-0" side="right">
              <div className="p-2">
                <p className="font-medium">Add items to iterate</p>
                <p className="text-muted-foreground mt-1 text-xs">
                  Fill in the Items field in the loop settings
                </p>
              </div>
              <div className="flex flex-col border-t">
                <button
                  className="hover:bg-muted flex items-center gap-2 px-3 py-2 text-left text-xs"
                  onClick={() => {
                    const input = document.querySelector(
                      `#${CSS.escape(`${id}-inlineItems`)}`
                    ) as HTMLInputElement | null
                    if (input) {
                      input.focus()
                    }
                  }}
                >
                  <ArrowLeft className="size-3" />
                  Focus Items input
                </button>
              </div>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Input connection hint for empty loop */}
        {showInputHint && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <div className="loop-hint-input group bg-primary/15 hover:bg-primary/25 absolute top-1/2 left-2 -translate-y-1/2 cursor-help rounded-full p-1.5 shadow-sm transition-[background-color,box-shadow,scale] hover:scale-110 hover:shadow-md">
                <ArrowLeft className="text-primary size-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs" side="right">
              <p className="font-medium">Loop Input</p>
              <p className="text-muted-foreground mt-1 text-xs">
                Connect nodes here to feed data into the loop. External nodes must connect to this
                handle, not directly to nodes inside.
              </p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Right Handle */}
        <Handle
          position={Position.Right}
          type="source"
          className={cn(
            "!bg-background !-right-2 !size-3 !border-2",
            isOutputHighlighted ? "loop-handle-highlighted" : "!border-primary"
          )}
        />

        {/* Output connection hint for empty loop */}
        {showOutputHint && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <div className="loop-hint-output group bg-primary/15 hover:bg-primary/25 absolute top-1/2 right-2 -translate-y-1/2 cursor-help rounded-full p-1.5 shadow-sm transition-[background-color,box-shadow,scale] hover:scale-110 hover:shadow-md">
                <ArrowRight className="text-primary size-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs" side="left">
              <p className="font-medium">Loop Output</p>
              <p className="text-muted-foreground mt-1 text-xs">
                Connect from here to use the loop's results. This outputs the combined result of all
                iterations.
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </NodeOutputPopover>
  )
}
